import React, { useCallback, useEffect, useState } from 'react';
import { Dashboard } from '../api/client';
import { AuthUser, DashboardData, World } from '../types';
import { Icon } from './ui/Icon';
import { Empty, Loading, Note, Section, StatusPill } from './ui/primitives';
import { ActivityList } from './world/ActivityTab';
import { timeAgo } from '../utils/format';
import { getRegionFriendlyName } from '../utils/regionUtils';

interface Props {
  me: AuthUser;
  worlds: World[];
  worldsLoading: boolean;
  onSelect: (id: string) => void;
}

const VIP_SHOP_URL = 'https://discord.com/channels/804414667551670335/shop';
const REFRESH_MS = 10000;

/** Player dashboard: VIP status, counts, owned worlds and recent activity. */
const Home: React.FC<Props> = ({ me, worlds, worldsLoading, onSelect }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await Dashboard.user());
      setError(null);
    } catch (e) {
      console.error('Failed to load dashboard', e);
      setError('Could not load your dashboard right now.');
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const owned = me.minecraftUuid ? worlds.filter((w) => w.ownerId === me.minecraftUuid) : [];
  const isVIP = data ? data.isVIP : !!me.isVIP;

  return (
    <div className="main2">
      <div className="home-head">
        <h1>Hi, {me.user.username}.</h1>
        <div className="sub">{owned.length === 0 ? 'Your private worlds will show up here.' : `You own ${owned.length} world${owned.length === 1 ? '' : 's'}.`}</div>
      </div>
      <div className="page" style={{ paddingTop: 22 }}>
        {isVIP ? (
          <Note tone="ok">Welcome to Bakery Worlds hub! You can create and manage your own private Minecraft worlds. You can also manage your plots here.</Note>
        ) : (
          <Note tone="warn">
            You can access this site for your existing worlds. To create new worlds, become a VIP member.
            <a className="btn btn--sm" href={VIP_SHOP_URL} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 12 }}>
              Become a VIP <Icon name="external" size={12} />
            </a>
          </Note>
        )}
        {error && <Note tone="danger">{error}</Note>}

        {data ? (
          <div className="stats" style={{ marginBottom: 6 }}>
            <div className="stat">
              <small>Your worlds</small>
              <b>{data.stats.worldCount}</b>
              <i>worlds you own</i>
            </div>
            <div className="stat">
              <small>Your backups</small>
              <b>{data.stats.backupCount}</b>
              <i>across all your worlds</i>
            </div>
          </div>
        ) : (
          !error && <Loading />
        )}

        <Section title="Your worlds" blurb="Pick a world to see its settings, backups, activity and map.">
          {worldsLoading && owned.length === 0 ? (
            <Loading />
          ) : owned.length === 0 ? (
            <Empty icon="globe" title="No worlds yet">
              Create one in-game with <span className="kbd">/pw</span> or ask in Discord.
            </Empty>
          ) : (
            <div className="wgrid">
              {owned.map((w) => (
                <button type="button" key={w.id} className="wcard" onClick={() => onSelect(w.id)}>
                  <span className="nm">{w.name}</span>
                  <StatusPill status={w.status} />
                  <span className="meta">
                    <span>
                      <b>{getRegionFriendlyName(w.region)}</b>
                    </span>
                    {w.lastUsedVersion && <span>{w.lastUsedVersion}</span>}
                    <span>{w.lastAccessedAt ? `played ${timeAgo(w.lastAccessedAt)}` : 'never played'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Section>

        {data && (
          <Section title="Recent activity" blurb="What happened across your worlds in the last 7 days.">
            <ActivityList audits={data.stats.recentActivity || []} showWorld limit={12} />
          </Section>
        )}
      </div>
    </div>
  );
};

export default Home;
